import { useCallback, useEffect, useMemo, useState } from "react";
import type { AggregationKind, ColumnAggregate } from "./table/types";

export interface AggregationsApi {
  /** Devuelve true si la agregación con esa key está activa. */
  isEnabled: (key: string) => boolean;
  /** Activa o desactiva una agregación. */
  toggle: (key: string) => void;
  /** Activa explícitamente. */
  enable: (key: string) => void;
  /** Desactiva explícitamente. */
  disable: (key: string) => void;
  /** Keys activas, en orden de inserción. */
  enabledKeys: string[];
  /** Tipo de agregación actualmente seleccionado para una key. */
  getKind: (key: string) => AggregationKind | undefined;
  /** Cambia el tipo de agregación para una key activa (persiste en localStorage). */
  setKind: (key: string, kind: AggregationKind) => void;
  /** Resetea todas las agregaciones a vacío. */
  reset: () => void;
  /** true si hay al menos una agregación activa. */
  hasEnabled: boolean;
}

const ENABLED_KEY_PREFIX = "table-aggregations-";
const KIND_KEY_PREFIX = "table-aggregation-kinds-";

/**
 * Persiste qué agregaciones están activas y qué tipo se aplica a cada una,
 * por tabla en localStorage. Mismo patrón que useColumnPin.
 */
export function useAggregations(
  tableId: string | undefined,
  availableKeys: string[],
): AggregationsApi {
  const enabledStorageKey = tableId ? `${ENABLED_KEY_PREFIX}${tableId}` : null;
  const kindStorageKey = tableId ? `${KIND_KEY_PREFIX}${tableId}` : null;

  const [enabled, setEnabled] = useState<string[]>(() => {
    if (typeof window === "undefined" || !enabledStorageKey) return [];
    try {
      const raw = window.localStorage.getItem(enabledStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((k) => typeof k === "string") : [];
    } catch {
      return [];
    }
  });

  const [kinds, setKinds] = useState<Record<string, AggregationKind>>(() => {
    if (typeof window === "undefined" || !kindStorageKey) return {};
    try {
      const raw = window.localStorage.getItem(kindStorageKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return {};
      const out: Record<string, AggregationKind> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (
          typeof v === "string" &&
          (v === "sum" || v === "avg" || v === "count" || v === "min" || v === "max")
        ) {
          out[k] = v;
        }
      }
      return out;
    } catch {
      return {};
    }
  });

  useEffect(() => {
    if (typeof window === "undefined" || !enabledStorageKey) {
      setEnabled([]);
      return;
    }
    try {
      const raw = window.localStorage.getItem(enabledStorageKey);
      if (!raw) {
        setEnabled([]);
        return;
      }
      const parsed = JSON.parse(raw);
      setEnabled(Array.isArray(parsed) ? parsed.filter((k) => typeof k === "string") : []);
    } catch {
      setEnabled([]);
    }
  }, [enabledStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined" || !kindStorageKey) {
      setKinds({});
      return;
    }
    try {
      const raw = window.localStorage.getItem(kindStorageKey);
      if (!raw) {
        setKinds({});
        return;
      }
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") {
        setKinds({});
        return;
      }
      const out: Record<string, AggregationKind> = {};
      for (const [k, v] of Object.entries(parsed)) {
        if (
          typeof v === "string" &&
          (v === "sum" || v === "avg" || v === "count" || v === "min" || v === "max")
        ) {
          out[k] = v;
        }
      }
      setKinds(out);
    } catch {
      setKinds({});
    }
  }, [kindStorageKey]);

  const cleanEnabled = useMemo(
    () => enabled.filter((k) => availableKeys.includes(k)),
    [enabled, availableKeys],
  );

  // Limpia kinds huérfanos (sin enabled).
  const cleanKinds = useMemo(() => {
    const out: Record<string, AggregationKind> = {};
    for (const k of cleanEnabled) {
      const v = kinds[k];
      if (v) out[k] = v;
    }
    return out;
  }, [cleanEnabled, kinds]);

  const persistEnabled = useCallback(
    (next: string[]) => {
      setEnabled(next);
      if (enabledStorageKey && typeof window !== "undefined") {
        try {
          window.localStorage.setItem(enabledStorageKey, JSON.stringify(next));
        } catch {
          // ignore
        }
      }
    },
    [enabledStorageKey],
  );

  const persistKinds = useCallback(
    (next: Record<string, AggregationKind>) => {
      setKinds(next);
      if (kindStorageKey && typeof window !== "undefined") {
        try {
          window.localStorage.setItem(kindStorageKey, JSON.stringify(next));
        } catch {
          // ignore
        }
      }
    },
    [kindStorageKey],
  );

  const isEnabled = useCallback(
    (key: string) => cleanEnabled.includes(key),
    [cleanEnabled],
  );

  const enable = useCallback(
    (key: string) => {
      if (!availableKeys.includes(key) || cleanEnabled.includes(key)) return;
      persistEnabled([...cleanEnabled, key]);
    },
    [availableKeys, cleanEnabled, persistEnabled],
  );

  const disable = useCallback(
    (key: string) => {
      persistEnabled(cleanEnabled.filter((k) => k !== key));
    },
    [cleanEnabled, persistEnabled],
  );

  const toggle = useCallback(
    (key: string) => {
      if (cleanEnabled.includes(key)) disable(key);
      else enable(key);
    },
    [cleanEnabled, enable, disable],
  );

  const getKind = useCallback(
    (key: string) => cleanKinds[key],
    [cleanKinds],
  );

  const setKind = useCallback(
    (key: string, kind: AggregationKind) => {
      if (!cleanEnabled.includes(key)) return;
      persistKinds({ ...cleanKinds, [key]: kind });
    },
    [cleanEnabled, cleanKinds, persistKinds],
  );

  const reset = useCallback(() => {
    persistEnabled([]);
    persistKinds({});
  }, [persistEnabled, persistKinds]);

  return {
    isEnabled,
    toggle,
    enable,
    disable,
    enabledKeys: cleanEnabled,
    getKind,
    setKind,
    reset,
    hasEnabled: cleanEnabled.length > 0,
  };
}

/**
 * Resultado calculado de una agregación.
 */
export interface AggregationResult {
  key: string;
  label: string;
  kind: AggregationKind;
  kinds: AggregationKind[];
  format: "currency" | "number";
  currency: string;
  value: number;
  display: string;
}

/**
 * Helper puro: dado un set de filas y una definición de agregación,
 * calcula el valor agregado y su representación formateada.
 *
 * - count: cuenta todas las filas (ignora accessor).
 * - sum/avg/min/max: extrae `accessor(row) ?? row[key]` por fila, filtra NaN/null.
 * - `kind` opcional: si se omite, usa el primero declarado en `aggregate.kinds`.
 */
export function computeAggregation<T>(
  rows: T[],
  key: string,
  aggregate: ColumnAggregate<T>,
  kindOverride?: AggregationKind,
): AggregationResult {
  const extract = aggregate.accessor ?? ((row: any) => row[key as any]);
  const label = aggregate.label ?? key;
  const format = aggregate.format ?? "number";
  const currency = aggregate.currency ?? "HNL";
  const kind: AggregationKind =
    kindOverride && aggregate.kinds.includes(kindOverride)
      ? kindOverride
      : aggregate.kinds[0];

  let value: number;

  switch (kind) {
    case "count": {
      value = rows.length;
      break;
    }
    case "sum": {
      value = rows.reduce((acc, row) => {
        const v = extract(row);
        return typeof v === "number" && !isNaN(v) ? acc + v : acc;
      }, 0);
      break;
    }
    case "avg": {
      let sum = 0;
      let n = 0;
      for (const row of rows) {
        const v = extract(row);
        if (typeof v === "number" && !isNaN(v)) {
          sum += v;
          n++;
        }
      }
      value = n === 0 ? 0 : sum / n;
      break;
    }
    case "min": {
      let min = Number.POSITIVE_INFINITY;
      for (const row of rows) {
        const v = extract(row);
        if (typeof v === "number" && !isNaN(v) && v < min) min = v;
      }
      value = min === Number.POSITIVE_INFINITY ? 0 : min;
      break;
    }
    case "max": {
      let max = Number.NEGATIVE_INFINITY;
      for (const row of rows) {
        const v = extract(row);
        if (typeof v === "number" && !isNaN(v) && v > max) max = v;
      }
      value = max === Number.NEGATIVE_INFINITY ? 0 : max;
      break;
    }
  }

  let display: string;
  if (format === "currency") {
    try {
      display = new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency,
      }).format(value);
    } catch {
      display = `${currency} ${value.toFixed(2)}`;
    }
  } else {
    display = new Intl.NumberFormat("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  return {
    key,
    label,
    kind,
    kinds: aggregate.kinds,
    format,
    currency,
    value,
    display,
  };
}
